import type { RouterOutputs } from "@project/api";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import { useTRPC } from "../../../trpc";
import { Button, Menu, Stack } from "@mantine/core";
import { SimpleBreadcrumbs } from "../../../components/SimpleBreadcrumbs";
import { useNavigate } from "@tanstack/react-router";
import { ActionCard } from "../../../components/ActionCard";
import { IconTrash, IconUserEdit } from "@tabler/icons-react";
import { useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePathAccountName } from "../../../hooks/router";
import { AgGridTable } from "../../../components/AgGridTable.js";
import { useMapkaNotification } from "../../../hooks/notifications.js";

export type APIKey = RouterOutputs["getApiKeys"][number];

export function APIKeys() {
  const intl = useIntl();
  const navigate = useNavigate();
  const trpc = useTRPC();
  const accountName = usePathAccountName();
  const queryClient = useQueryClient();
  const mapkaNotification = useMapkaNotification();

  const { data = [] } = useQuery(trpc.getApiKeys.queryOptions());

  const { mutate } = useMutation(trpc.deleteApiKey.mutationOptions());

  const handleDelete = useCallback((id: number, name: string) => {
    mutate({ id }, {
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.getApiKeys.queryFilter());
        mapkaNotification({
          title: intl.formatMessage({
            defaultMessage: "API key deleted",
            id: "notification.apiKeyDeleted",
          }),
          message: intl.formatMessage({
            defaultMessage: "Successfully deleted: {name}",
            id: "notification.apiKeyDeletedMessage",
          }, { name }),
          variant: "success",
        });
      },
    });
  }, [mutate]);

  const ActionsCellRenderer = ({ data: rowData }: ICellRendererParams<APIKey>) => {
    if (!rowData) return null;
    return (
      <Menu shadow="md">
        <Menu.Target>
          <Button variant="secondary" size="xs">
            Actions
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<IconTrash />}
            onClick={() => {
              handleDelete(rowData.id, rowData.name);
            }}
          >
            <FormattedMessage defaultMessage="Delete" id="button.delete" />
          </Menu.Item>
          <Menu.Item
            onClick={() => {
              void navigate({
                to: "/$accountName/admin/edit-api-key/$id",
                params: { accountName, id: rowData.id.toString() },
                state: {
                  apiKey: rowData,
                } as Record<string, unknown>,
              });
            }}
            leftSection={<IconUserEdit />}
          >

            <FormattedMessage defaultMessage="Edit" id="button.edit" />
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

    );
  };
  const columnDefs: ColDef<APIKey>[] = useMemo(() => [
    {
      field: "name",
      headerName: intl.formatMessage({ defaultMessage: "Name", id: "column.name" }),
    },
    {
      field: "type",
      headerName: intl.formatMessage({ defaultMessage: "Type", id: "column.type" }),
      valueGetter: (params) => {
        if (params.data?.type === "public") {
          return intl.formatMessage({ defaultMessage: "Public", id: "label.public" });
        }
        if (params.data?.type === "default") {
          return intl.formatMessage({ defaultMessage: "Default", id: "label.default" });
        }
        else {
          return intl.formatMessage({ defaultMessage: "Secret", id: "label.secret" });
        }
      },
    },
    {
      field: "key",
      headerName: intl.formatMessage({ defaultMessage: "Key", id: "column.key" }),
      sortable: false,
    },
    {
      field: "createdAt",
      headerName: intl.formatMessage({ defaultMessage: "Created at", id: "column.createdAt" }),
      sortable: true,
      cellDataType: "dateTimeString",
    },
    {
      headerName: "Actions",
      cellRenderer: ActionsCellRenderer,
      sortable: false,
      filter: false,
      maxWidth: 180,
    },
  ], [intl, handleDelete]);

  return (
    <Stack gap={16} h="100%">
      <SimpleBreadcrumbs
        parent={intl.formatMessage({ defaultMessage: "Manage account", id: "navigation.manageAccount" })}
        current={intl.formatMessage({ defaultMessage: "API keys", id: "navigation.apiKeys" })}
        description={intl.formatMessage({ defaultMessage: "Manage your API keys, revoke permissions and access, and create new API keys.", id: "text.apiKeysDescription" })}
      />

      <ActionCard
        title={intl.formatMessage({ defaultMessage: "API keys", id: "title.apiKeys" })}
        subTitle={intl.formatMessage({ defaultMessage: "Manage access and permissions for your projects.", id: "text.apiKeysManageDescription" })}
      >
        <Button
          variant="primary"
          onClick={() => void navigate({
            to: "/$accountName/admin/new-api-key",
            params: { accountName },
          })}
        >
          <FormattedMessage defaultMessage="Create API key" id="button.createApiKey" />
        </Button>
      </ActionCard>

      <Stack py="xs">
        <AgGridTable
          rowData={data}
          columnDefs={columnDefs}
          domLayout="autoHeight"
          autoSizeStrategy={{
            type: "fitGridWidth",
            defaultMinWidth: 100,
          }}
          suppressHorizontalScroll={false}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
          }}
        />
      </Stack>
    </Stack>
  );
}
